import {
  readWorkflowPublishedInspection,
  type WorkflowPublishedInspection,
  type WorkflowPublishedInspectionSource,
  type WorkflowSavedPresence,
} from './workflow-published-inspection';

export type WorkflowPublishedWorkSectionId =
  | 'responsibility'
  | 'requirements'
  | 'forms'
  | 'actions'
  | 'otherCommands';
export type WorkflowPublishedWorkState =
  | 'absent'
  | 'empty'
  | 'available'
  | 'retained-unknown'
  | 'unavailable';
export interface WorkflowPublishedWorkFact {
  id: string;
  label: string;
  value: string;
  presence: WorkflowSavedPresence;
  issues: readonly string[];
  raw: unknown;
}
export interface WorkflowPublishedWorkDetailRow {
  id: string;
  index: number;
  label: string;
  fields: readonly WorkflowPublishedWorkFact[];
  issues: readonly string[];
  raw: unknown;
}
export interface WorkflowPublishedWorkDetailGroup {
  id: string;
  label: string;
  state: WorkflowPublishedWorkState;
  presence: WorkflowSavedPresence;
  rows: readonly WorkflowPublishedWorkDetailRow[];
  issues: readonly string[];
  raw: unknown;
}
export interface WorkflowPublishedWorkRow extends WorkflowPublishedWorkDetailRow {
  description: string;
  state: 'available' | 'retained-unknown';
  groups: readonly WorkflowPublishedWorkDetailGroup[];
}
export interface WorkflowPublishedWorkSection {
  id: WorkflowPublishedWorkSectionId;
  label: string;
  state: WorkflowPublishedWorkState;
  presence: WorkflowSavedPresence;
  rows: readonly WorkflowPublishedWorkRow[];
  issues: readonly string[];
  raw: unknown;
}
export interface WorkflowPublishedWorkInspection {
  context: string;
  state: 'available' | 'unavailable';
  provenance: WorkflowPublishedInspection['provenance'];
  issues: readonly string[];
  target: { nodeIndex: number; nodeId: string | null };
  node: {
    index: number;
    id: string | null;
    label: string;
    typeCode: string;
    issues: readonly string[];
    raw: unknown;
  } | null;
  sections: Readonly<
    Record<WorkflowPublishedWorkSectionId, WorkflowPublishedWorkSection>
  >;
}

type Saved = Record<string, unknown>;
const record = (value: unknown): value is Saved =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const object = (value: unknown): Saved => (record(value) ? value : {});
const presence = (value: Saved, key: string): WorkflowSavedPresence =>
  !Object.hasOwn(value, key)
    ? 'absent'
    : value[key] === null
      ? 'null'
      : 'value';
const nonempty = (value: unknown): value is string =>
  typeof value === 'string' && !!value.trim();
const positive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
const copy = (value: unknown): unknown => structuredClone(value);
function display(value: unknown): string {
  if (value === undefined) return 'Not declared';
  if (value === null) return 'Explicitly null';
  if (typeof value === 'string')
    return value === '' ? 'Empty saved text' : value;
  try {
    return JSON.stringify(value) ?? 'Unsupported saved value';
  } catch {
    return 'Unsupported saved value';
  }
}
function facts(
  value: Saved,
  keys: readonly (readonly [string, string])[],
  prefix: string,
): WorkflowPublishedWorkFact[] {
  return keys.map(([key, label]) => ({
    id: `${prefix}:${key}`,
    label,
    value: display(value[key]),
    presence: presence(value, key),
    issues: [],
    raw: copy(value[key]),
  }));
}
const propertyFacts = (
  value: Saved,
  prefix: string,
): WorkflowPublishedWorkFact[] =>
  facts(
    value,
    Object.keys(value).map((key) => [key, key] as const),
    prefix,
  );
function extraIssues(value: Saved, allowed: readonly string[]): string[] {
  return Object.keys(value).some((key) => !allowed.includes(key))
    ? [
        'Additional saved properties are retained without interpreting their meaning.',
      ]
    : [];
}
function collectionState(
  savedPresence: WorkflowSavedPresence,
  raw: unknown,
  rows: readonly { issues: readonly string[] }[],
  issues: readonly string[],
): WorkflowPublishedWorkState {
  if (savedPresence !== 'value') return 'absent';
  if (
    !Array.isArray(raw) ||
    issues.length ||
    rows.some((row) => row.issues.length)
  )
    return 'retained-unknown';
  return rows.length ? 'available' : 'empty';
}
function group(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  project: (raw: unknown, index: number) => WorkflowPublishedWorkDetailRow,
): WorkflowPublishedWorkDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key),
    rows = Array.isArray(raw) ? raw.map(project) : [];
  const issues =
    savedPresence === 'value' && !Array.isArray(raw)
      ? [
          'This saved collection has an unsupported shape. Its original value is retained.',
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    state: collectionState(savedPresence, raw, rows, issues),
    presence: savedPresence,
    rows,
    issues,
    raw: copy(raw),
  };
}
function detailRow(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  keys?: readonly (readonly [string, string])[],
): WorkflowPublishedWorkDetailRow {
  const value = object(raw),
    issues = record(raw)
      ? keys
        ? extraIssues(
            value,
            keys.map(([key]) => key),
          )
        : []
      : ['This saved entry has an unsupported shape.'];
  return {
    id: `${prefix}:${index}`,
    index,
    label,
    fields: keys
      ? facts(value, keys, `${prefix}:${index}`)
      : propertyFacts(value, `${prefix}:${index}`),
    issues,
    raw: copy(raw),
  };
}
const sectionLabels: Record<WorkflowPublishedWorkSectionId, string> = {
  responsibility: 'Responsibility',
  requirements: 'Requirements',
  forms: 'Forms',
  actions: 'Actions',
  otherCommands: 'Other saved commands',
};
function section(
  id: WorkflowPublishedWorkSectionId,
  savedPresence: WorkflowSavedPresence,
  raw: unknown,
  rows: WorkflowPublishedWorkRow[],
  issues: string[] = [],
): WorkflowPublishedWorkSection {
  return {
    id,
    label: sectionLabels[id],
    presence: savedPresence,
    state: collectionState(savedPresence, raw, rows, issues),
    rows,
    issues,
    raw: copy(raw),
  };
}
function pinKey(value: unknown): string | null {
  const key = object(object(value)['key']);
  return ['provider', 'kind', 'code'].every((name) => nonempty(key[name])) &&
    positive(key['version'])
    ? JSON.stringify([
        key['provider'],
        key['kind'],
        key['code'],
        key['version'],
      ])
    : null;
}
function fullPin(value: unknown): boolean {
  return (
    pinKey(value) !== null &&
    ['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every((key) =>
      nonempty(object(value)[key]),
    )
  );
}
function samePin(left: unknown, right: unknown): boolean {
  return (
    fullPin(left) &&
    fullPin(right) &&
    pinKey(left) === pinKey(right) &&
    ['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every(
      (key) => object(left)[key] === object(right)[key],
    )
  );
}
const commandKeys = [
  'capability',
  'requirements',
  'outcomes',
  'captureForm',
  'formConfiguration',
  'inputBindings',
  'outputBindings',
];
const policyFields = [
  ['id', 'Policy ID'],
  ['scopeCode', 'Policy scope'],
  ['nodeId', 'Step ID'],
  ['assignmentBasisCode', 'Assignment basis'],
  ['assignmentModeCode', 'Assignment mode'],
  ['initialQueueCode', 'Initial queue'],
  ['eligibleRoleCodes', 'Eligible role codes'],
  ['eligibleQueueCodes', 'Eligible queue codes'],
  ['unassignedBehaviorCode', 'Unassigned behavior'],
  ['fallbackBehaviorCode', 'Fallback behavior'],
  ['returnBehaviorCode', 'Return behavior'],
  ['reentryBehaviorCode', 'Reentry behavior'],
] as const;

function responsibility(
  document: Saved,
  behavior: Saved,
  nodeId: string | null,
  uniqueNode: boolean,
  declarationPresent: boolean,
): WorkflowPublishedWorkSection {
  const raw = document['assignmentPolicies'],
    policies: unknown[] = Array.isArray(raw) ? raw : [],
    reference = behavior['assignmentPolicyId'];
  const attached = policies.filter(
    (value) => record(value) && nodeId !== null && value['nodeId'] === nodeId,
  );
  const issues: string[] = [];
  if (
    presence(document, 'assignmentPolicies') === 'value' &&
    !Array.isArray(raw)
  )
    issues.push(
      'The saved policy container is opaque; no step policy is inferred.',
    );
  if (attached.length !== 1 && (attached.length || reference != null))
    issues.push(
      'The saved step responsibility does not resolve to one attached policy.',
    );
  const rows = policies.flatMap(
    (rawPolicy, index): WorkflowPublishedWorkRow[] => {
      const policy = object(rawPolicy);
      if (
        record(rawPolicy) &&
        !attached.includes(rawPolicy) &&
        !(reference != null && policy['id'] === reference)
      )
        return [];
      const rowIssues = extraIssues(
        policy,
        policyFields.map(([key]) => key),
      );
      if (!record(rawPolicy))
        rowIssues.push(
          'This policy entry is opaque and cannot be attached to a step.',
        );
      if (
        !uniqueNode ||
        attached.length !== 1 ||
        attached[0] !== rawPolicy ||
        policy['scopeCode'] !== 'step' ||
        !nonempty(policy['id']) ||
        policies.filter((item) => record(item) && item['id'] === policy['id'])
          .length !== 1 ||
        (declarationPresent && reference !== policy['id'])
      )
        rowIssues.push(
          'An exact unique saved step-policy linkage is not established. Workflow policies are not a fallback.',
        );
      if (
        (policy['assignmentBasisCode'] != null &&
          policy['assignmentBasisCode'] !== 'roles') ||
        !['queue_only', 'claim', 'direct', 'claim_or_direct'].includes(
          display(policy['assignmentModeCode']),
        ) ||
        !['require_queue', 'allow_unassigned'].includes(
          display(policy['unassignedBehaviorCode']),
        ) ||
        !['block', 'initial_queue', 'unassigned'].includes(
          display(policy['fallbackBehaviorCode']),
        ) ||
        ![
          'retain_assignment',
          'retain_queue',
          'initial_queue',
          'unassigned',
        ].includes(display(policy['returnBehaviorCode'])) ||
        ![
          'retain_assignment',
          'retain_queue',
          'initial_queue',
          'unassigned',
        ].includes(display(policy['reentryBehaviorCode']))
      )
        rowIssues.push(
          'Some saved assignment codes use an unsupported value; their original values are retained.',
        );
      return [
        {
          id: `saved-policy:${index}`,
          index,
          label: nonempty(policy['id'])
            ? policy['id']
            : `Saved policy ${index + 1}`,
          description:
            policy['assignmentBasisCode'] == null
              ? 'Queue-based legacy semantics; no explicit assignment basis is recorded here.'
              : 'Saved responsibility configuration; eligible people are not resolved.',
          state: rowIssues.length ? 'retained-unknown' : 'available',
          fields: [
            ...facts(
              behavior,
              [['assignmentPolicyId', 'Saved step policy reference']],
              `saved-policy:${index}:reference`,
            ),
            ...facts(policy, policyFields, `saved-policy:${index}`),
          ],
          groups: [],
          issues: rowIssues,
          raw: copy(rawPolicy),
        },
      ];
    },
  );
  const result = section(
    'responsibility',
    presence(document, 'assignmentPolicies'),
    raw,
    rows,
    issues,
  );
  const snapshot: Saved = {};
  if (Object.hasOwn(behavior, 'assignmentPolicyId'))
    snapshot['assignmentPolicyId'] = copy(reference);
  if (Object.hasOwn(document, 'assignmentPolicies'))
    snapshot['assignmentPolicies'] = copy(raw);
  result.raw = snapshot;
  if (reference != null && !rows.length) result.state = 'retained-unknown';
  return result;
}

function requirementRows(
  behavior: Saved,
  commands: readonly unknown[],
): WorkflowPublishedWorkSection {
  const raw = behavior['completionRequirements'];
  const rows: WorkflowPublishedWorkRow[] = Array.isArray(raw)
    ? raw.map((value, index): WorkflowPublishedWorkRow => {
        const requirement = object(value),
          settings = requirement['settings'],
          issues = extraIssues(requirement, [
            'id',
            'provider',
            'version',
            'settingsVersion',
            'settingsHash',
            'settings',
          ]);
        let label = nonempty(requirement['id'])
          ? requirement['id']
          : `Saved requirement ${index + 1}`;
        if (
          !record(value) ||
          !nonempty(requirement['id']) ||
          !nonempty(requirement['provider']) ||
          !positive(requirement['version']) ||
          !positive(requirement['settingsVersion']) ||
          !record(settings)
        )
          issues.push(
            'The saved requirement has an unsupported shape; its complete configuration is retained.',
          );
        if (
          nonempty(requirement['id']) &&
          raw.filter((item) => record(item) && item['id'] === requirement['id'])
            .length !== 1
        )
          issues.push(
            'This requirement ID is duplicated. Each saved entry is shown separately.',
          );
        if (
          ![
            'workflow.form-completed',
            'workflow.document-upload',
            'workflow.decision-recorded',
            'workflow.quorum-evaluated',
          ].includes(String(requirement['provider'])) ||
          requirement['version'] !== 1 ||
          requirement['settingsVersion'] !== 1
        )
          issues.push(
            'This provider/settings version is retained without claiming current provider support.',
          );
        if (requirement['provider'] === 'workflow.form-completed') {
          const formId = object(settings)['formId'];
          issues.push(...extraIssues(object(settings), ['formId']));
          const matches = commands.filter(
            (command) =>
              record(command) &&
              record(command['captureForm']) &&
              command['captureForm']['id'] === formId,
          );
          if (!nonempty(formId) || matches.length !== 1)
            issues.push(
              'The saved form reference is missing or ambiguous. No form or evidence is inferred.',
            );
          if (
            requirement['version'] === 1 &&
            requirement['settingsVersion'] === 1
          ) {
            const title =
              matches.length === 1
                ? object(object(matches[0])['captureForm'])['title']
                : null;
            label = nonempty(title) ? `${title} saved` : 'Form completed';
          }
        }
        return {
          id: `saved-requirement:${index}`,
          index,
          label,
          description: `${display(requirement['provider'])} · version ${display(requirement['version'])} · configuration only, not evidence`,
          state: issues.length ? 'retained-unknown' : 'available',
          fields: [
            ...facts(
              requirement,
              [
                ['id', 'Requirement ID'],
                ['provider', 'Provider'],
                ['version', 'Provider version'],
                ['settingsVersion', 'Settings version'],
                ['settingsHash', 'Settings hash'],
              ],
              `saved-requirement:${index}`,
            ),
            ...propertyFacts(
              object(settings),
              `saved-requirement:${index}:settings`,
            ),
          ],
          groups: [],
          issues,
          raw: copy(value),
        };
      })
    : [];
  return section(
    'requirements',
    presence(behavior, 'completionRequirements'),
    raw,
    rows,
  );
}

function formGroup(
  form: unknown,
  provided: boolean,
  prefix: string,
  payload: unknown,
): WorkflowPublishedWorkDetailGroup {
  const saved = object(form),
    version = saved['version'];
  const result = group(
    saved,
    'fields',
    provided ? 'Saved field overrides' : 'Saved capture fields',
    prefix,
    (raw, index) => {
      const field = object(raw),
        id = field[provided ? 'name' : 'id'];
      const row = detailRow(
        raw,
        index,
        prefix,
        nonempty(field['label'])
          ? field['label']
          : nonempty(id)
            ? id
            : `Saved field ${index + 1}`,
        provided
          ? [
              ['name', 'Field name'],
              ['label', 'Label override'],
              ['required', 'Required override'],
              ['control', 'Control override'],
              ['maxLength', 'Maximum length'],
              ['optionSource', 'Option source'],
              ['options', 'Saved options'],
              ['layout', 'Saved layout'],
              ['lookup', 'Saved lookup binding'],
            ]
          : [
              ['id', 'Field ID'],
              ['label', 'Field label'],
              ['type', 'Field type'],
              ['required', 'Required'],
              ['maxLength', 'Maximum length'],
              ['minimum', 'Minimum'],
              ['maximum', 'Maximum'],
              ['options', 'Saved options'],
              ['visibleWhen', 'Saved visibility condition'],
              ['layout', 'Saved layout'],
              ['lookup', 'Saved lookup binding'],
            ],
      );
      const issues = [...row.issues];
      if (!nonempty(id))
        issues.push('This saved field identity is unavailable.');
      if (
        Array.isArray(saved['fields']) &&
        nonempty(id) &&
        saved['fields'].filter(
          (item) => record(item) && item[provided ? 'name' : 'id'] === id,
        ).length !== 1
      )
        issues.push(
          'This saved field identity is duplicated; the original entries remain separate.',
        );
      if (
        !provided &&
        (![
          'text',
          'textarea',
          'number',
          'integer',
          'date',
          'boolean',
          'select',
        ].includes(String(field['type'])) ||
          typeof field['required'] !== 'boolean')
      )
        issues.push(
          'This capture field uses an unsupported saved type or required flag.',
        );
      return { ...row, issues };
    },
  );
  const issues = [
    ...result.issues,
    ...extraIssues(
      saved,
      provided ? ['version', 'fields'] : ['version', 'id', 'title', 'fields'],
    ),
  ];
  // Inspection preserves later/unknown combinations; it never widens an authoring gate.
  const supported =
    version === 1 ||
    (version === 2 && ['1.19', '1.20', '1.21', '1.22'].includes(String(payload))) ||
    (version === 3 &&
      (provided
        ? ['1.21', '1.22'].includes(String(payload))
        : ['1.20', '1.21', '1.22'].includes(String(payload))));
  if (!record(form) || !supported)
    issues.push(
      'This saved outer/form format combination is retained without generating a form.',
    );
  if (!provided && (!nonempty(saved['id']) || !nonempty(saved['title'])))
    issues.push('The saved capture form identity or title is unavailable.');
  return {
    ...result,
    state:
      issues.length || result.state === 'retained-unknown'
        ? 'retained-unknown'
        : result.state,
    issues,
    raw: copy(form),
  };
}

function commandSections(
  document: Saved,
  behavior: Saved,
  detailRaw: unknown,
): Pick<
  WorkflowPublishedWorkInspection['sections'],
  'forms' | 'actions' | 'otherCommands'
> {
  const detail = object(detailRaw),
    raw = behavior['commands'],
    commands: unknown[] = Array.isArray(raw) ? raw : [],
    interaction = detail['mainInteraction'];
  const mainMatches =
    interaction != null
      ? commands.flatMap((command, index) =>
          samePin(object(command)['capability'], object(interaction)['command'])
            ? [index]
            : [],
        )
      : [];
  const forms: WorkflowPublishedWorkRow[] = [],
    actions: WorkflowPublishedWorkRow[] = [],
    other: WorkflowPublishedWorkRow[] = [];
  const formIssues: string[] = [];
  if (detailRaw != null && !record(detailRaw))
    formIssues.push(
      'The saved detail container is opaque; a main interaction cannot be inferred.',
    );
  if (
    interaction != null &&
    (!record(interaction) ||
      !nonempty(interaction['code']) ||
      !positive(interaction['version']) ||
      extraIssues(interaction, ['code', 'version', 'command']).length)
  )
    formIssues.push(
      'The saved main interaction has an unsupported outer shape; its exact properties are retained.',
    );
  if (interaction != null && mainMatches.length !== 1)
    formIssues.push(
      'The saved main interaction does not identify one exact command. No main form is guessed.',
    );
  commands.forEach((value, index) => {
    const command = object(value),
      reference = command['capability'],
      pin = object(reference),
      key = object(pin['key']);
    const issues = extraIssues(command, commandKeys);
    if (!record(value) || !fullPin(reference) || key['kind'] !== 'command')
      issues.push('This saved command reference is incomplete or unsupported.');
    if (
      !Array.isArray(command['requirements']) ||
      !Array.isArray(command['outcomes'])
    )
      issues.push(
        'The saved command requirements or outcomes do not have their declared collection shape.',
      );
    issues.push(
      ...extraIssues(pin, [
        'key',
        'inputSchemaHash',
        'outputSchemaHash',
        'contractHash',
      ]),
      ...extraIssues(key, ['provider', 'kind', 'code', 'version']),
    );
    const savedPins = document['capabilityReferences'];
    const linked = Array.isArray(savedPins)
      ? savedPins.filter(
          (item) => pinKey(item) !== null && pinKey(item) === pinKey(reference),
        )
      : [];
    if (linked.length !== 1 || !samePin(linked[0], reference))
      issues.push(
        'One exact saved dependency pin is not established; no current installation is inferred.',
      );
    if (
      pinKey(reference) !== null &&
      commands.filter(
        (item) => pinKey(object(item)['capability']) === pinKey(reference),
      ).length !== 1
    )
      issues.push('This command key is duplicated in the saved command list.');
    const groups = [
      group(
        command,
        'requirements',
        'Saved command requirements',
        `saved-command:${index}`,
        (entry, childIndex) => ({
          id: `saved-command:${index}:requirement:${childIndex}`,
          index: childIndex,
          label: display(entry),
          fields: [],
          issues:
            typeof entry === 'string'
              ? []
              : ['This saved requirement code has an unsupported shape.'],
          raw: copy(entry),
        }),
      ),
      group(
        command,
        'outcomes',
        'Saved outcomes',
        `saved-command:${index}`,
        (entry, childIndex) =>
          detailRow(
            entry,
            childIndex,
            `saved-command:${index}:outcome`,
            display(object(entry)['label']),
            [
              ['code', 'Outcome code'],
              ['label', 'Outcome label'],
              ['reasonPolicy', 'Reason policy'],
              ['destinationNodeId', 'Destination step ID'],
            ],
          ),
      ),
      group(
        command,
        'inputBindings',
        'Saved input bindings',
        `saved-command:${index}`,
        (entry, childIndex) =>
          detailRow(
            entry,
            childIndex,
            `saved-command:${index}:input`,
            `Input binding ${childIndex + 1}`,
          ),
      ),
      group(
        command,
        'outputBindings',
        'Saved output bindings',
        `saved-command:${index}`,
        (entry, childIndex) =>
          detailRow(
            entry,
            childIndex,
            `saved-command:${index}:output`,
            `Output binding ${childIndex + 1}`,
          ),
      ),
    ];
    const fields = [
      ...facts(
        key,
        [
          ['provider', 'Provider'],
          ['kind', 'Capability kind'],
          ['code', 'Command code'],
          ['version', 'Command version'],
        ],
        `saved-command:${index}:key`,
      ),
      ...facts(
        pin,
        [
          ['inputSchemaHash', 'Input schema hash'],
          ['outputSchemaHash', 'Output schema hash'],
          ['contractHash', 'Contract hash'],
        ],
        `saved-command:${index}:pin`,
      ),
    ];
    const capture = command['captureForm'],
      provided = command['formConfiguration'];
    const isForm =
      capture != null ||
      provided != null ||
      (mainMatches.length === 1 && mainMatches[0] === index);
    if (isForm) {
      if (mainMatches.length === 1 && mainMatches[0] === index) {
        fields.push(
          ...facts(
            detail,
            [['mainInteraction', 'Saved main interaction']],
            `saved-command:${index}:interaction`,
          ),
          ...facts(
            object(interaction),
            [
              ['code', 'Interaction code'],
              ['version', 'Interaction version'],
            ],
            `saved-command:${index}:interaction`,
          ),
        );
        issues.push(...formIssues);
      }
      if (capture != null) {
        fields.push(
          ...facts(
            object(capture),
            [
              ['version', 'Capture form version'],
              ['id', 'Capture form ID'],
              ['title', 'Capture form title'],
            ],
            `saved-command:${index}:capture`,
          ),
        );
        groups.unshift(
          formGroup(
            capture,
            false,
            `saved-command:${index}:capture`,
            document['payloadSchemaVersion'],
          ),
        );
      }
      if (provided != null) {
        fields.push(
          ...facts(
            object(provided),
            [['version', 'Override format version']],
            `saved-command:${index}:provided`,
          ),
        );
        groups.unshift(
          formGroup(
            provided,
            true,
            `saved-command:${index}:provided`,
            document['payloadSchemaVersion'],
          ),
        );
      }
      if (capture != null && provided != null)
        issues.push(
          'Both capture settings and provided overrides are saved; their combined meaning is retained for inspection.',
        );
      fields.push(
        ...facts(
          command,
          [
            ['captureForm', 'Saved capture form'],
            ['formConfiguration', 'Saved provided overrides'],
          ],
          `saved-command:${index}:forms`,
        ),
      );
    }
    const rowIssues = [
      ...issues,
      ...groups.flatMap((group) => [
        ...group.issues,
        ...group.rows.flatMap((row) => row.issues),
      ]),
    ];
    const row: WorkflowPublishedWorkRow = {
      id: `saved-command:${index}`,
      index,
      label:
        isForm && nonempty(object(capture)['title'])
          ? (object(capture)['title'] as string)
          : nonempty(key['code'])
            ? key['code']
            : `Saved command ${index + 1}`,
      description: isForm
        ? capture != null
          ? 'Saved capture form configuration; no answers or runtime form are loaded.'
          : 'Saved provided-form overrides/main interaction only. Remaining installed fields are not recorded in this document.'
        : 'Saved command configuration; no execution or current authorization is implied.',
      state: rowIssues.length ? 'retained-unknown' : 'available',
      fields,
      groups,
      issues: rowIssues,
      raw: copy(value),
    };
    if (isForm) forms.push(row);
    else if (
      fullPin(reference) &&
      key['provider'] === 'workflow' &&
      key['kind'] === 'command' &&
      key['code'] === 'workflow.step.complete' &&
      key['version'] === 1 &&
      !extraIssues(command, commandKeys).length &&
      Array.isArray(command['requirements']) &&
      command['requirements'].every((item) => typeof item === 'string') &&
      Array.isArray(command['outcomes']) &&
      command['outcomes'].every(
        (item) =>
          record(item) &&
          ['code', 'label', 'destinationNodeId'].every((name) =>
            nonempty(item[name]),
          ) &&
          ['optional', 'required'].includes(String(item['reasonPolicy'])),
      )
    )
      actions.push({ ...row, label: 'Complete step' });
    else
      other.push({
        ...row,
        state: 'retained-unknown',
        description:
          'Work ownership is not established by this saved command. It is retained separately and is not counted as a Work action.',
        issues: [
          ...row.issues,
          'No installed provider or label is used to infer ownership.',
        ],
      });
  });
  const savedPresence = presence(behavior, 'commands');
  const formPresence =
    savedPresence === 'value'
      ? savedPresence
      : Object.hasOwn(detail, 'mainInteraction')
        ? presence(detail, 'mainInteraction')
        : detailRaw != null && !record(detailRaw)
          ? 'value'
          : savedPresence;
  const formSection = section('forms', formPresence, raw, forms, formIssues),
    formSnapshot: Saved = {};
  if (Object.hasOwn(behavior, 'commands')) formSnapshot['commands'] = copy(raw);
  if (Object.hasOwn(detail, 'mainInteraction'))
    formSnapshot['mainInteraction'] = copy(interaction);
  if (detailRaw != null && !record(detailRaw))
    formSnapshot['detail'] = copy(detailRaw);
  formSection.raw = formSnapshot;
  return {
    forms: formSection,
    actions: section('actions', savedPresence, raw, actions),
    otherCommands: section('otherCommands', savedPresence, raw, other),
  };
}

/** Immutable saved Work facts only; canonical/hash admission remains the registry's responsibility. */
export function readWorkflowPublishedWorkInspection(
  source: WorkflowPublishedInspectionSource,
  target: { nodeIndex: number; nodeId: string | null },
): WorkflowPublishedWorkInspection {
  const admitted = readWorkflowPublishedInspection(source);
  const unavailable = (
    id: WorkflowPublishedWorkSectionId,
  ): WorkflowPublishedWorkSection => ({
    id,
    label: sectionLabels[id],
    state: 'unavailable',
    presence: 'absent',
    rows: [],
    issues: [],
    raw: null,
  });
  const sections: Record<
    WorkflowPublishedWorkSectionId,
    WorkflowPublishedWorkSection
  > = {
    responsibility: unavailable('responsibility'),
    requirements: unavailable('requirements'),
    forms: unavailable('forms'),
    actions: unavailable('actions'),
    otherCommands: unavailable('otherCommands'),
  };
  const result: WorkflowPublishedWorkInspection = {
    context: JSON.stringify([admitted.context, target]),
    state: 'unavailable',
    provenance: structuredClone(admitted.provenance),
    issues: [...admitted.issues],
    target: { nodeIndex: target.nodeIndex, nodeId: target.nodeId },
    node: null,
    sections,
  };
  if (admitted.state !== 'available') {
    result.issues = [
      ...admitted.issues,
      'Saved Work content is withheld because the selected canonical document was not admitted.',
    ];
    return result;
  }
  const document = object(source.definitionDocument),
    nodes = document['nodes'];
  if (
    !Number.isSafeInteger(target.nodeIndex) ||
    target.nodeIndex < 0 ||
    !Array.isArray(nodes) ||
    target.nodeIndex >= nodes.length
  ) {
    result.issues = [
      'The exact saved node index is unavailable. No other step is substituted.',
    ];
    return result;
  }
  const rawNode = nodes[target.nodeIndex],
    node = object(rawNode),
    nodeId = typeof node['id'] === 'string' ? node['id'] : null;
  if (
    !record(rawNode) ||
    nodeId !== target.nodeId ||
    node['typeCode'] !== 'human_step'
  ) {
    result.issues = [
      'The selected saved index and human-step identity do not match. Work content is withheld.',
    ];
    return result;
  }
  const uniqueNode =
    nonempty(nodeId) &&
    nodes.filter((item) => record(item) && item['id'] === nodeId).length === 1;
  const nodeIssues = uniqueNode
    ? []
    : [
        'This saved step identity is missing or duplicated. Index-specific content is retained; cross-references are not guessed.',
      ];
  result.state = 'available';
  result.context = JSON.stringify([admitted.context, target, document]);
  result.node = {
    index: target.nodeIndex,
    id: nodeId,
    label:
      typeof node['displayName'] === 'string'
        ? display(node['displayName'])
        : `Saved step ${target.nodeIndex + 1}`,
    typeCode: node['typeCode'],
    issues: nodeIssues,
    raw: copy(rawNode),
  };
  result.issues = nodeIssues;
  const declaration = node['declaration'],
    behaviorRaw = object(declaration)['behavior'],
    behavior = object(behaviorRaw);
  sections.responsibility = responsibility(
    document,
    behavior,
    nodeId,
    uniqueNode,
    declaration != null,
  );
  if (
    (declaration != null && !record(declaration)) ||
    (behaviorRaw != null && !record(behaviorRaw))
  ) {
    for (const id of [
      'requirements',
      'forms',
      'actions',
      'otherCommands',
    ] as const)
      sections[id] = {
        ...sections[id],
        state: 'retained-unknown',
        presence: 'value',
        issues: [
          'The saved Work declaration is opaque. No empty collections or generated settings are inferred.',
        ],
        raw: copy(declaration),
      };
    return result;
  }
  if (declaration == null || behaviorRaw == null) {
    for (const id of [
      'requirements',
      'forms',
      'actions',
      'otherCommands',
    ] as const)
      sections[id] = {
        ...sections[id],
        state: 'absent',
        presence:
          declaration == null
            ? presence(node, 'declaration')
            : presence(object(declaration), 'behavior'),
        raw: copy(declaration == null ? declaration : behaviorRaw),
      };
    if (record(declaration))
      sections.forms = commandSections(
        document,
        behavior,
        declaration['detail'],
      ).forms;
    return result;
  }
  const commands = Array.isArray(behavior['commands'])
    ? behavior['commands']
    : [];
  sections.requirements = requirementRows(behavior, commands);
  Object.assign(
    sections,
    commandSections(document, behavior, object(declaration)['detail']),
  );
  return result;
}
